import { Fragment, useEffect, useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import DatePicker from 'react-datepicker'
import debounce from 'lodash/debounce'
import { DownloadSimple } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { Alert } from '@/components/ui/alert'
import { buildCsvFilename, downloadCsv } from '@/utils/file'

import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import Filter from '../../Filter/Filter'
import SearchField from '../../FormLegacy/FormFieldsV2/SearchField'
import PaginationFooter from '../../PaginationFooter/PaginationFooter'
import '../../../styles/components/filterButtons/filter-buttons.scss'
import MaintenanceRequestPreview from '../MaintenanceRequestPreview/MaintenanceRequestPreview'
import {
  fetchMaintenanceRequests,
  selectRequests,
  setSelectedRequest,
  selectStats,
  selectRequest,
} from '../../../reducers/maintenanceReducer'
import { getPropertyFilterOptions } from '../../Organisation/CommunicationPortal/helpers'
import api from '../../../api/api'

import {
  getMaintenanceRequestTabs,
  getMaintenanceRequestStatuses,
  getMaintenanceRequestPriorities,
} from './helpers'
import MaintenanceRequestsDataReports from './components/MaintenanceRequestsDataReports'
import MaintenanceRequestsList from './components/MaintenanceRequestsList'

const MaintenanceRequests = () => {
  const { t } = useTranslation(['maintenance'])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  const maintenanceRequestTabs = getMaintenanceRequestTabs(t)
  const maintenanceRequestStatuses = getMaintenanceRequestStatuses(t)
  const maintenanceRequestPriorities = getMaintenanceRequestPriorities(t)

  const [isLoading, setLoading] = useState(false)
  const [hasFetchError, setHasFetchError] = useState(false)
  const [search, setSearch] = useState('')
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [selectedTab, setSelectedTab] = useState(maintenanceRequestTabs[0])
  const [sortBy, setSortBy] = useState('DESC')
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [prevPageNumber, setPrevPageNumber] = useState(pageNumber)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [dateRange, setDateRange] = useState([null, null])
  const [startDate, endDate] = dateRange
  const [statusFilters, setStatusFilters] = useState([])
  const [prioritiesFilters, setPrioritiesFilters] = useState([])
  const [yardiFilters, setYardiFilters] = useState([])
  const [yardiPropId, setYardiPropId] = useState()

  const requests = useSelector(selectRequests)
  const stats = useSelector(selectStats)
  const selectedRequest = useSelector(selectRequest)
  const selectedBuilding = useSelector(selectActiveBuilding)

  const isYardiProperty = !!(
    selectedBuilding?.yardiManagementId && selectedBuilding?.yardiPropertyId
  )

  const handleFetchMaintenanceRequests = async (id, values) => {
    const requestData = {
      search,
      type: selectedTab.value,
      dateFrom: dateRange[0],
      dateTo: dateRange[1],
      status: statusFilters.map((status) => status.value),
      yardiPropertyId: yardiPropId ? [yardiPropId.text] : [],
      limit: showOnPage,
      skip: pageNumber * showOnPage - showOnPage,
      updatedAtSort: sortBy,
      ...values,
    }
    if (isYardiProperty) {
      requestData.priority = prioritiesFilters.map((priority) => priority.value)
    }

    setLoading(true)
    try {
      const requests = await dispatch(fetchMaintenanceRequests(id, requestData))
      setLoading(false)
      setHasFetchError(false)
      return requests
    } catch (error) {
      setLoading(false)
      setHasFetchError(true)
      throw error
    }
  }

  const onCSVDownload = async () => {
    try {
      const requestData = {
        buildingId: selectedBuilding.id,
        search,
        type: selectedTab.value,
        dateFrom: dateRange[0],
        dateTo: dateRange[1],
        status: statusFilters.map((status) => status.value),
        yardiPropertyId: yardiPropId ? [yardiPropId.text] : [],
        limit: showOnPage,
        skip: pageNumber * showOnPage - showOnPage,
        updatedAtSort: sortBy,
      }
      if (isYardiProperty) {
        requestData.priority = prioritiesFilters.map((priority) => priority.value)
      }
      const { data } = await api.maintenanceDownloadCSVReport(requestData)

      downloadCsv({
        blob: data,
        filename: buildCsvFilename({
          building: selectedBuilding?.name || null,
          page: 'maintenance',
          dateRange: [dateRange?.[0], dateRange?.[1]],
          timezone: selectedBuilding?.timezone,
        }),
      })
    } catch ({ message }) {
      console.error(message)
    }
  }

  const getRequestsBySearch = useMemo(() => {
    return debounce((id, values) => {
      handleFetchMaintenanceRequests(id, values).catch(() => {
        /* empty - error already shown by the inline error alert */
      })
    }, 800)
  }, [])

  const yardiPropertyOptions = useMemo(
    () =>
      yardiFilters.length
        ? getPropertyFilterOptions(yardiFilters.map(({ yardiPropertyId }) => yardiPropertyId))
        : [],
    [yardiFilters],
  )

  useEffect(() => {
    if (selectedBuilding && selectedBuilding.id && !selectedBuilding?.id) {
      navigate(`${location.pathname}/${selectedBuilding.id}`)
    }
  }, [selectedBuilding, selectedBuilding?.id])

  useEffect(() => {
    if (prevSearchValue !== search) {
      // Only clear selected request state if viewing a specific request, don't navigate
      if (selectedRequest) {
        dispatch(setSelectedRequest(null))
      }
      const searchParams = {
        search,
        dateFrom: dateRange[0],
        dateTo: dateRange[1],
        type: selectedTab.value,
        status: statusFilters.map((status) => status.value),
        yardiPropertyId: yardiPropId ? [yardiPropId.text] : [],
        limit: showOnPage,
        skip: pageNumber * showOnPage - showOnPage,
        updatedAtSort: sortBy,
      }
      if (isYardiProperty) {
        searchParams.priority = prioritiesFilters.map((priority) => priority.value)
      }
      getRequestsBySearch(selectedBuilding?.id, searchParams)
    }
    setPrevSearchValue(search)
  }, [
    search,
    getRequestsBySearch,
    selectedBuilding?.id,
    selectedRequest,
    dispatch,
    dateRange,
    selectedTab,
    statusFilters,
    yardiPropId,
    showOnPage,
    pageNumber,
    sortBy,
    isYardiProperty,
    prioritiesFilters,
  ])

  useEffect(() => {
    let count = 0
    switch (selectedTab.value) {
      case 'pending':
        count = stats.pendingCount
        break
      case 'scheduled':
        count = stats.scheduledCount
        break
      case 'completed':
        count = stats.completedCount
        break
      default:
        count = stats.pendingCount + stats.scheduledCount + stats.completedCount
        break
    }
    setPaginationItemsCount(count || 0)
  }, [stats])

  useEffect(() => {
    ;(async () => {
      if (selectedBuilding?.id) {
        setSelectedRequest(null)
        setPageNumber(1)
        setPrevPageNumber(1)

        try {
          const requests = await handleFetchMaintenanceRequests(selectedBuilding?.id, { skip: 0 })
          if (!isYardiProperty) {
            setYardiPropId(null)
          }
          if (selectedBuilding?.id) {
            setSelectedRequest(requests.find((req) => req.id.toString() === selectedBuilding?.id))
          }
        } catch {
          /* empty - error already shown by the inline error alert */
        }
      }
    })()
  }, [selectedBuilding?.id, selectedBuilding?.id, selectedBuilding])

  useEffect(() => {
    if (isYardiProperty) {
      api.getYardiPropertyIdsByBuildingId(selectedBuilding.id).then((response) => {
        setYardiFilters(response.data)
      })
    }
  }, [isYardiProperty])

  useEffect(() => {
    ;(async () => {
      if (prevPageNumber !== pageNumber) {
        if (selectedBuilding?.id) {
          try {
            await handleFetchMaintenanceRequests(selectedBuilding?.id)
          } catch {
            /* empty - error already shown by the inline error alert */
          }
          setPrevPageNumber(pageNumber)
        }
      }
    })()
  }, [pageNumber])

  useEffect(() => {
    ;(async () => {
      if (
        selectedBuilding?.id &&
        ((dateRange[0] && dateRange[1]) || (!dateRange[0] && !dateRange[1])) &&
        !selectedRequest
      ) {
        try {
          await handleFetchMaintenanceRequests(selectedBuilding?.id, { skip: 0 })
        } catch {
          /* empty - error already shown by the inline error alert */
        }
      }
    })()
  }, [
    statusFilters,
    prioritiesFilters,
    yardiPropId,
    dateRange,
    selectedTab,
    sortBy,
    selectedRequest,
  ])

  useEffect(() => {
    return () => {
      dispatch(setSelectedRequest(null))
    }
  }, [])

  const getCount = (tab) => {
    if (tab.value === '') {
      const count = stats.pendingCount + stats.scheduledCount + stats.completedCount
      return isNaN(count) ? 0 : count
    } else {
      return stats[`${tab.value}Count`]
    }
  }

  return selectedBuilding?.id ? (
    <Fragment>
      {!selectedRequest ? (
        <Fragment>
          <MaintenanceRequestsDataReports buildingId={selectedBuilding?.id} />
          <div className={'maintenance-requests'}>
            <div className={'maintenance-requests__wrap h100'}>
              <div className={'maintenance-requests__filter-menu'}>
                <div className="maintenance-requests__filter-menu__buttons">
                  {maintenanceRequestTabs.map((tabFilter) => (
                    <button
                      className={`message-type-filter ${
                        selectedTab && tabFilter.id === selectedTab.id ? 'active' : ''
                      }`}
                      key={tabFilter.id}
                      onClick={() => setSelectedTab(tabFilter)}
                      disabled={isLoading}
                    >
                      <p style={{ display: 'inline-block' }}>{tabFilter.text} </p>
                      <div className="maintenance-requests-count">
                        <span>{getCount(tabFilter)}</span>
                      </div>
                    </button>
                  ))}
                </div>
                <DatePicker
                  className={'input'}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText={t('maintenance:requests.dateRangePlaceholder')}
                  isClearable={true}
                  onChange={(update) => setDateRange(update)}
                />
                <Filter
                  placeholder={
                    statusFilters && statusFilters.length
                      ? maintenanceRequestStatuses
                          .filter((status) => statusFilters.includes(status))
                          .map((status) => status.text)
                          .join(', ')
                      : t('maintenance:requests.filters.statusFallback')
                  }
                  options={maintenanceRequestStatuses}
                  disabled={isLoading}
                  value={statusFilters || []}
                  name={'MessageStatusFilter'}
                  additionalClass="maintenance-requests__filter-menu__filter"
                  config={{ multiple: true }}
                  variant={'rounded'}
                  onChange={(e) => setStatusFilters(e.target.value)}
                />
                {isYardiProperty && (
                  <Filter
                    placeholder={
                      prioritiesFilters && prioritiesFilters.length
                        ? maintenanceRequestPriorities
                            .filter((status) => prioritiesFilters.includes(status))
                            .map((priority) => priority.text)
                            .join(', ')
                        : t('maintenance:requests.filters.priorityFallback')
                    }
                    options={maintenanceRequestPriorities}
                    disabled={isLoading}
                    value={prioritiesFilters || []}
                    name={'priority'}
                    additionalClass="maintenance-requests__filter-menu__filter"
                    config={{ multiple: true }}
                    variant={'rounded'}
                    onChange={(e) => setPrioritiesFilters(e.target.value)}
                  />
                )}
                {!!yardiFilters.length && isYardiProperty && (
                  <Filter
                    name="yardiPropertyId"
                    placeholder={
                      yardiPropId ? yardiPropId.text : t('maintenance:requests.filters.selectPhase')
                    }
                    additionalClass="maintenance-requests_phase-dropdown"
                    options={yardiPropertyOptions}
                    value={yardiPropId ? [yardiPropId] : []}
                    onChange={(e) => {
                      if (e.target.value === yardiPropId) {
                        setYardiPropId(null)
                      } else {
                        setYardiPropId(e.target.value)
                      }
                    }}
                    config={{
                      multiple: false,
                      hasSearch: true,
                    }}
                  />
                )}
                <div className={'maintenance-requests__filter-menu__search'}>
                  <SearchField
                    placeholder={t('maintenance:requests.searchPlaceholder')}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value)
                    }}
                  />
                </div>
                <button
                  onClick={onCSVDownload}
                  className="button button-black-outline button-transparent"
                >
                  <DownloadSimple size={18} />
                  {t('maintenance:requests.csv')}
                </button>
              </div>

              {hasFetchError && !isLoading ? (
                <Alert
                  type="error"
                  title={t('maintenance:requests.list.error.title')}
                  message={t('maintenance:requests.list.error.description')}
                />
              ) : (
                <MaintenanceRequestsList
                  requests={requests}
                  isLoading={isLoading}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  isYardiProperty={isYardiProperty}
                />
              )}
              {!hasFetchError && requests && requests.length ? (
                <PaginationFooter
                  showOnPage={showOnPage}
                  pageNumber={pageNumber}
                  paginationItemsCount={paginationItemsCount}
                  setSiteLoading={setLoading}
                  setShowOnPage={setShowOnPage}
                  setPageNumber={setPageNumber}
                />
              ) : null}
            </div>
          </div>
        </Fragment>
      ) : (
        <MaintenanceRequestPreview
          data={selectedRequest}
          buildingData={selectedBuilding}
          handleClose={() => dispatch(setSelectedRequest(null))}
        />
      )}
    </Fragment>
  ) : (
    <p>{t('maintenance:requests.selectBuilding')}</p>
  )
}

export default MaintenanceRequests
